import React from 'react';
import CommonTable from '../../../Components/CommonTable';
import PageHeading from '../../../Components/PageHeading';
import { SubHeading, Body, label as labelPreset, fontSize, fontWeight } from '../../../Constants/Fonts';

// RbacGuide — "Risk Registry: Roles, Stages & Capabilities". A static reference
// (no data fetch) that documents the workflow pipeline, per-stage RACI, the
// roles, enterprise controls, per-org customisation, and the capability matrix.
// Mirrors the legacy portal's guide, rebuilt on the shared design tokens.

// Role key → { label, colour } for the pipeline / stage-detail / matrix headers.
const ROLE_BY_KEY = {
    risk_admin: { label: 'Risk Admin', color: 'var(--color-chart-purple, #a855f7)' },
    risk_manager: { label: 'Risk Manager', color: 'var(--color-primary)' },
    risk_owner: { label: 'Risk Owner', color: 'var(--color-medium)' },
    risk_reviewer: { label: 'Risk Reviewer', color: 'var(--color-info)' },
    business_owner: { label: 'Business Owner', color: 'var(--color-success)' },
    risk_committee: { label: 'Risk Committee', color: 'var(--color-critical)' },
    ciso: { label: 'CISO (Read-Only)', color: 'var(--color-chart-purple, #7c3aed)' },
};

// The 7-stage lifecycle every risk flows through. SLA values are the product
// defaults (an org can override them under Workflow); the guide shows defaults.
const STAGES = [
    {
        key: 'identification', name: 'Identification', owner: 'risk_manager', sla: 3,
        purpose: 'Capture a new risk with enough context to be assessable.',
        entry: 'A risk is registered (manually, from a scan, or imported). The system auto-emails the assigned owner with a single-use acknowledgement link.',
        exit: 'A complete risk record exists with title, threat, vulnerability, and owner. Owner acknowledgement runs in parallel.',
    },
    {
        key: 'assessment', name: 'Assessment', owner: 'risk_manager', sla: 5,
        purpose: 'Score inherent likelihood × impact and capture KRIs.',
        entry: 'Risk has all identification fields populated.',
        exit: 'A current RiskAssessment record exists for the risk.',
    },
    {
        key: 'treatment', name: 'Treatment', owner: 'risk_owner', sla: 10,
        purpose: 'The owner designs a plan to bring residual risk into appetite.',
        entry: 'Risk has been assessed.',
        exit: 'At least one RiskTreatmentPlan exists for the risk.',
    },
    {
        key: 'review', name: 'Review', owner: 'risk_reviewer', sla: 5,
        purpose: 'Independent reviewer verifies the treatment plan is credible.',
        entry: 'A treatment plan was submitted.',
        exit: 'All treatment plans for the risk are marked verified.',
    },
    {
        key: 'acceptance', name: 'Acceptance', owner: 'business_owner', sla: 5,
        purpose: 'Residual exposure is formally accepted (severity-tiered: BU owner for low/medium, Risk Committee for high/critical).',
        entry: 'All treatment plans verified.',
        exit: 'Approver signs off; risk moves to monitoring.',
    },
    {
        key: 'monitoring', name: 'Monitoring', owner: 'risk_manager', sla: null,
        purpose: 'Watch the risk against KRIs and triggers; re-assess if conditions change.',
        entry: 'Risk accepted.',
        exit: 'Closure conditions met, or re-assessment kicks risk back to assessment.',
    },
    {
        key: 'closed', name: 'Closed', owner: null, sla: null,
        purpose: 'Terminal state. The risk has materialised, been fully mitigated, or is no longer relevant. Closed by Risk Manager (or Risk Admin override) after all closure conditions are satisfied.',
        entry: 'Risk Manager invokes Close; system validates blockers. All stakeholders are notified.',
        exit: 'No further actions; record kept for audit.',
    },
];

const ROLES = [
    {
        key: 'risk_admin',
        summary: 'Configures the Risk Registry. Owns workflows, taxonomies, and role assignments.',
        responsibilities: ['Configure workflow stages, SLAs, categories, and risk appetite', 'Assign and revoke risk roles for team members', 'Override stalled stages when normal RACI cannot proceed', 'Maintain compliance framework mappings'],
    },
    {
        key: 'risk_manager',
        summary: 'Identifies and assesses risks, then hands off to owners for treatment.',
        responsibilities: ['Create and document new risks', 'Run inherent + residual assessments and KRIs', 'Re-open monitored risks when conditions change', 'Coordinate periodic reviews'],
    },
    {
        key: 'risk_owner',
        summary: 'Accountable individual for a risk. Designs and executes the treatment plan.',
        responsibilities: ['Define treatment strategy (mitigate / transfer / accept / avoid)', 'Plan controls, target dates, and residual targets', 'Submit treatment plans for review', 'Track action progress and evidence'],
    },
    {
        key: 'risk_reviewer',
        summary: 'Validates that treatment plans are credible before they go to acceptance.',
        responsibilities: ['Review treatment plans submitted by owners', 'Approve or send plans back for revision', 'Verify control coverage against the risk', 'Flag plans that miss residual targets'],
    },
    {
        key: 'business_owner',
        summary: 'Accepts low and medium residual risk on behalf of the business unit.',
        responsibilities: ['Accept residual risk for low / medium severity', 'Acknowledge BU exposure and appetite breaches', 'Sponsor controls funded from the BU budget'],
    },
    {
        key: 'risk_committee',
        summary: 'Tiered escalation. Accepts high and critical residual risk that exceeds BU authority.',
        responsibilities: ['Accept residual risk for high / critical severity', 'Review aggregated portfolio exposure', 'Authorise breaches above appetite thresholds', 'Report material risks to the board'],
    },
    {
        key: 'ciso',
        summary: 'Cross-BU executive oversight. Read-only on every risk, KRI, and audit log entry.',
        responsibilities: ['Monitor enterprise risk posture across BUs', 'Review the role audit log for control assurance', 'Brief the board on aggregate exposure', 'Has no stage-advancement authority'],
    },
];

const MATRIX_ROLES = ['risk_admin', 'risk_manager', 'risk_owner', 'risk_reviewer', 'business_owner', 'risk_committee', 'ciso'];

const CAPABILITIES = [
    { action: 'Configure workflow / appetite / categories', risk_admin: true },
    { action: 'Manage role assignments', risk_admin: true },
    { action: 'View role audit log', risk_admin: true, ciso: true },
    { action: 'Create / identify a risk', risk_admin: true, risk_manager: true },
    { action: 'Assess inherent + residual', risk_admin: true, risk_manager: true },
    { action: 'Author treatment plan', risk_admin: true, risk_owner: true },
    { action: 'Approve / reject treatment plan', risk_admin: true, risk_reviewer: true },
    { action: 'Accept residual risk (low / medium)', risk_admin: true, business_owner: true, risk_committee: true },
    { action: 'Accept residual risk (high / critical)', risk_admin: true, risk_committee: true },
    { action: 'Close monitored risk (all conditions met)', risk_admin: true, risk_manager: true },
    { action: 'Force-close (override blockers)', risk_admin: true },
    { action: 'Re-open monitored risk (back to assessment)', risk_admin: true, risk_manager: true },
    { action: 'Override stalled stage / SoD bypass', risk_admin: true },
    { action: 'Manage Business Units (CRUD)', risk_admin: true, risk_manager: true },
    { action: 'Resend owner-acknowledgement invite', risk_admin: true, risk_manager: true },
    { action: 'Read all risks, KRIs, KPIs', risk_admin: true, risk_manager: true, risk_owner: true, risk_reviewer: true, business_owner: true, risk_committee: true, ciso: true },
];

const ENTERPRISE_CONTROLS = [
    { color: 'var(--color-critical)', title: 'Tiered Acceptance', body: 'Severity routes the sign-off automatically: Low / Medium → Business Owner, High / Critical → Risk Committee. Each role’s acceptance queue is on its dashboard.' },
    { color: 'var(--color-primary)', title: 'Segregation of Duties (SoD)', body: 'At configured SoD stages (default: review), the acting user cannot also be the registered risk owner, the risk creator, or the linked project’s project manager.' },
    { color: 'var(--color-medium)', title: 'Time-Bounded Roles', body: 'Each role grant carries optional valid_from / valid_until dates. Outside the window the role is inactive — useful for vacation cover, contractors, and project-bounded grants.' },
    { color: 'var(--color-success)', title: 'Immutable Audit Logs', body: 'Two append-only trails: the role audit log (every grant / modify / revoke with before-and-after snapshots) and the risk field-change log (every mutation with the actor’s email). Meets SOX and ISO 27001 A.9.' },
    { color: 'var(--color-chart-purple, #7c3aed)', title: 'Owner Acknowledgement', body: 'Every new risk with an owner mints a single-use share token (default 30-day TTL) and emails a public link. Clicking Acknowledge or Decline consumes the token and notifies the creator.' },
    { color: 'var(--color-info)', title: 'Hierarchical BUs & Projects', body: 'Risks attach to a Business Unit (nested tree) and optionally a Project. The dashboard scope filter drills down by BU subtree or Project; every KPI, chart, and queue recomputes.' },
    { color: 'var(--color-critical)', title: 'Closure Conditions', body: 'A risk closes only when every treatment action is verified, residual ≤ appetite, all KRIs are normal, and acceptance is recorded. Only Risk Admin can force-close with overrides.' },
    { color: 'var(--color-medium)', title: 'Notifications & SLA Escalation', body: 'Every stage advance emails next-stage role holders and the owner. Approvals, rejections and closures notify all stakeholders. SLA breaches per stage dispatch escalation emails up the role chain.' },
    { color: 'var(--color-high)', title: 'Inherent vs Residual Risk', body: 'Each risk carries an inherent rating (pre-control, immutable) and a residual rating (current likelihood × impact, updated as treatments take effect). Reports show effectiveness as the delta.' },
    { color: 'var(--color-primary)', title: 'Workflow Validation on Save', body: 'Saving the workflow runs 15+ governance checks (type mismatches, required-artifact loops, missing reject paths, SoD gaps, tight SLAs). Errors block the save; warnings can be dismissed.' },
];

const CUSTOMISATION = [
    { color: 'var(--color-primary)', title: 'Workflow stages', body: 'Add, rename, reorder, or remove stages. Set per-stage owner role, SLA, type, next-stage transitions, and required-artifact gates.' },
    { color: 'var(--color-info)', title: 'State labels', body: 'Rename the 5 underlying states (open / in_progress / accepted / closed / transferred) for display. The system value never changes, so reports stay consistent.' },
    { color: 'var(--color-critical)', title: 'Severity colours', body: 'Override the colours for Low / Medium / High / Critical badges, posture bar, heatmap, and charts. Useful for brand alignment or accessibility palettes.' },
    { color: 'var(--color-medium)', title: 'Risk type routing', body: 'Map risk types to approver roles (e.g. cybersecurity → CISO). Applied at review/treatment/approve; severity-tiered acceptance still wins at acceptance.' },
    { color: 'var(--color-critical)', title: 'Severity thresholds & scale', body: 'Adjust the score boundaries mapping likelihood × impact to Low / Medium / High / Critical. Scale max is configurable (default 5 = ISO 5×5; range 2–10).' },
    { color: 'var(--color-chart-purple, #7c3aed)', title: 'Role display labels', body: 'Rename the 7 system roles for the customer’s vocabulary. Permissions and audit logs stay keyed off the system role; only the UI label changes.' },
    { color: 'var(--color-success)', title: 'Email branding', body: 'Custom from-address, brand name, and signature appended to every notification email. Defaults to the platform brand if blank.' },
    { color: 'var(--color-medium)', title: 'SoD enforcement stages', body: 'Pick which stages enforce Segregation of Duties (default: review only).' },
    { color: 'var(--color-high)', title: 'Business Unit code format', body: 'BU codes follow a strict standard (2–10 chars, A–Z 0–9 hyphens, starts with a letter). Live-normalised and validated server-side.' },
];

// Uppercase section-title style (Fonts.js label preset) — matches the dashboard.
const WIDGET_TITLE = labelPreset({
    color: 'var(--color-text-secondary)', fontWeight: fontWeight.bold,
    textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0,
});

const roleBadge = (key) => {
    const r = ROLE_BY_KEY[key];
    if (!r) return <Body as="span" variant={2} weight="medium" color="var(--color-text-muted)" style={{ fontSize: fontSize.sm }}>—</Body>;
    return (
        <Body
            as="span"
            className="rp1_guide_role_badge"
            style={{ color: r.color, borderColor: r.color, background: `color-mix(in srgb, ${r.color} 10%, transparent)` }}
        >
            {r.label}
        </Body>
    );
};

const Tile = ({ color, title, children }) => (
    <div className="rp1_guide_tile">
        <div className="rp1_guide_tile_head">
            <Body as="span" className="rp1_guide_tile_dot" style={{ background: color }} />
            <SubHeading variant={3} as="span" color="var(--color-text-primary)">{title}</SubHeading>
        </div>
        <Body variant={2} color="var(--color-text-secondary)" style={{ margin: '8px 0 0' }}>{children}</Body>
    </div>
);

const yesNoCell = (yes) => (
    <Body as="span" color={yes ? 'var(--color-success)' : 'var(--color-text-muted)'} weight={yes ? 'bold' : 'regular'} style={{ display: 'block', textAlign: 'center' }}>
        {yes ? '✓' : '–'}
    </Body>
);

export default function RbacGuide() {
    // ── Stage details table ──
    const stageHeaders = ['Stage', 'Purpose', 'Owner', 'SLA', 'Entry', 'Exit'];
    const stageRows = STAGES.map((s, i) => ({
        data: [
            <Body as="span" color="var(--color-text-primary)" weight="medium" style={{ whiteSpace: 'nowrap' }}>
                <Body as="span" variant={2} weight="medium" color="var(--color-text-muted)" style={{ fontSize: fontSize.sm, marginRight: 8, fontVariantNumeric: 'tabular-nums' }}>{String(i + 1).padStart(2, '0')}</Body>
                {s.name}
            </Body>,
            s.purpose,
            roleBadge(s.owner),
            <Body as="span" color="var(--color-text-secondary)" style={{ whiteSpace: 'nowrap' }}>{s.sla ? `${s.sla} days` : '—'}</Body>,
            s.entry,
            s.exit,
        ],
    }));

    // ── Capability matrix table ──
    const matrixHeaders = [
        'Action',
        ...MATRIX_ROLES.map((k) => (
            <Body as="span" key={k} variant={2} weight="medium" style={{ fontSize: fontSize.sm, display: 'block', textAlign: 'center' }}>{ROLE_BY_KEY[k].label}</Body>
        )),
    ];
    const matrixRows = CAPABILITIES.map((c) => ({
        data: [
            <Body as="span" color="var(--color-text-primary)">{c.action}</Body>,
            ...MATRIX_ROLES.map((k) => yesNoCell(!!c[k])),
        ],
    }));

    return (
        <div className="rp1_guide">
            <PageHeading
                title="Risk Registry: Roles, Stages & Capabilities"
                subtitle="A reference for who does what in the Risk Registry workflow. Every risk moves through the pipeline below; each stage has a single responsible role."
            />

            {/* Workflow pipeline — reference stepper (not a live risk's progress). */}
            <div className="rp1_widget">
                <div className="rp1_widget_head"><h3 style={WIDGET_TITLE}>Workflow Pipeline</h3></div>
                <div className="rp1_guide_pipeline">
                    {STAGES.map((s, i) => (
                        <React.Fragment key={s.key}>
                            <div className="rp1_guide_step">
                                <div className="rp1_guide_step_num">{i + 1}</div>
                                <Body as="span" color="var(--color-text-primary)" weight="medium" style={{ marginTop: 8 }}>{s.name}</Body>
                                <Body as="span" variant={2} color="var(--color-text-muted)" style={{ marginTop: 2 }}>
                                    {s.owner ? ROLE_BY_KEY[s.owner].label : '—'}
                                </Body>
                                {s.sla && <Body as="span" variant={2} color="var(--color-text-muted)">SLA {s.sla}d</Body>}
                            </div>
                            {i < STAGES.length - 1 && <Body as="span" className="rp1_guide_step_conn" />}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* Stage details */}
            <div className="rp1_widget">
                <div className="rp1_widget_head"><h3 style={WIDGET_TITLE}>Stage Details</h3></div>
                <CommonTable headers={stageHeaders} rows={stageRows} noPadding noScroll />
            </div>

            {/* Roles */}
            <div className="rp1_widget">
                <div className="rp1_widget_head"><h3 style={WIDGET_TITLE}>Roles</h3></div>
                <div className="rp1_guide_tiles">
                    {ROLES.map((r) => (
                        <div key={r.key} className="rp1_guide_tile">
                            <div className="rp1_guide_tile_head">
                                <Body as="span" className="rp1_guide_tile_dot" style={{ background: ROLE_BY_KEY[r.key].color }} />
                                <SubHeading variant={3} as="span" color="var(--color-text-primary)">{ROLE_BY_KEY[r.key].label}</SubHeading>
                            </div>
                            <Body variant={2} color="var(--color-text-secondary)" style={{ margin: '8px 0 0' }}>{r.summary}</Body>
                            <ul className="rp1_guide_tile_list">
                                {r.responsibilities.map((t, i) => (
                                    <li key={i}><Body as="span" variant={2} color="var(--color-text-secondary)">{t}</Body></li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>

            {/* Enterprise controls */}
            <div className="rp1_widget">
                <div className="rp1_widget_head"><h3 style={WIDGET_TITLE}>Enterprise Controls</h3></div>
                <div className="rp1_guide_tiles">
                    {ENTERPRISE_CONTROLS.map((t, i) => <Tile key={i} color={t.color} title={t.title}>{t.body}</Tile>)}
                </div>
            </div>

            {/* Per-org customisation */}
            <div className="rp1_widget">
                <div className="rp1_widget_head"><h3 style={WIDGET_TITLE}>Per-Org Customisation</h3></div>
                <Body variant={2} color="var(--color-text-muted)" style={{ margin: '0 0 16px' }}>
                    Everything below lives in Workflow / Customisation (Risk Admin only). Underlying system values stay stable for reports and integrations; only the display / behaviour layer changes per customer.
                </Body>
                <div className="rp1_guide_tiles">
                    {CUSTOMISATION.map((t, i) => <Tile key={i} color={t.color} title={t.title}>{t.body}</Tile>)}
                </div>
            </div>

            {/* Capability matrix */}
            <div className="rp1_widget">
                <div className="rp1_widget_head"><h3 style={WIDGET_TITLE}>Capability Matrix</h3></div>
                <CommonTable headers={matrixHeaders} rows={matrixRows} noPadding />
                <Body variant={2} color="var(--color-text-muted)" style={{ margin: '12px 0 0' }}>
                    ✓ = role can perform the action. Risk Admin can act on any stage as an override; the other roles are gated to the stage they own.
                </Body>
            </div>
        </div>
    );
}
